import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isObject from 'lodash/isObject';
import { connect } from 'react-redux';

import {
  updateBasicInfo as updateBasicInfoAction,
  updateBasicInfoOptions as updateBasicInfoOptionsAction,
} from '../actions';
import getCollegeService from '../../hemServices/get-college-service';
import decorateFieldWithValidation from '../validators';

// mapping for he_special_service_us for study options and services
const studyOptionsSpecialServiceUSValues = {
  academicCareerCounseling: '15',
  accommodationsForStudentsWithADHD: '16',
  assistanceForHearingImpaired: '21',
  assistanceForMobilityImpaired: '22',
  assistanceForVisuallyImpaired: '23',
  bookOnTapeForRFBD: '24',
  calculatorsAllowedInExams: '25',
  computersAllowedInExams: '26',
  dayCareServices: '27',
  dictionaryAllowedInExams: '28',
  ESL: '29',
  healthServicesAvailable: '30',
  internshipsWithPay: '31',
  internshipsWithoutPay: '32',
  jobPlacement: '33',
  LDSpecialists: '34',
  learningCenter: '35',
  libraryFacilities: '36',
  peerTutors: '37',
  professionalTutors: '38',
  readingMachine: '39',
  remedialServices: '40',
  spellcheckerAllowedInExam: '41',
  studentEmployment: '42',
  tapeRecordingInClass: '43',
  tapingOfBooksNotFromRFBD: '45',
  veteran: '46',
  studyAbroadCredit: '47',
  studyAbroadNonCredit: '48',
  graduateStudyAbroadCredit: '49',
  graduateStudyAbroadNonCredit: '50',
  studyAbroad: '51',
  graduateStudyAbroad: '52',
  aspergersAutismSupportServices: '8965',
  academicClubs: '4487',
  academicFratSor: '4488',
  business: '4489',
  choir: '4490',
  commuter: '4491',
  concertBand: '4492',
  environmental: '4493',
  gayAndLesbian: '4494',
  honorsAcademic: '4495',
  international: '4496',
  jazzBand: '4497',
  literaryMagazine: '4498',
  marchingBand: '4499',
  minorityCultural: '4500',
  minorityStudent: '4501',
  newspaper: '4502',
  orchestra: '4503',
  phiBetaKappa: '4504',
  physicallyChallenged: '4505',
  politicalActivism: '4506',
  preDental: '4507',
  preLaw: '4508',
  preMedical: '4509',
  preVeterinary: '4510',
  radioStation: '4511',
  religious: '4512',
  sportsIntramurals: '4513',
  studentGovernment: '4514',
  substanceAbuseSupport: '4515',
  televisionStation: '4516',
  theatreDrama: '4517',
  yearbook: '4518',
  phiThetaKappa: '4519',
};

export const updateHubsUI = ({
  eid,
  field,
  value,
}) => {
  const collegeService = getCollegeService();
  /* istanbul ignore else */
  if (collegeService && eid) {
    const fieldObject = collegeService.getField(eid, field);
    /* istanbul ignore else */
    if (fieldObject) {
      fieldObject.setValue(value);
    }
  }
};

export const getFieldMetadata = (collectionType, collectionReference) => {
  /* istanbul ignore next */
  const collegeService = getCollegeService();
  /* istanbul ignore next */
  if (collegeService) {
    if (collectionReference) {
      return collegeService.getEntityCollectionByReference(
        collectionReference.type,
        collectionReference.field,
        collectionReference.fields,
      );
    } else if (collectionType) {
      return collegeService.getEntityCollectionByType(collectionType.type, collectionType.fields);
    }
  }
  /* istanbul ignore next */
  return null;
};

export const formatOptions = (options) => Object.keys(options)
  .map((key) => ({
    value: key,
    text: String(options[key]).trim(),
    raw: options[key],
  }));

export const hasChildWithOptions = (data) => Object.keys(data).some((key) => {
  if (data[key] === undefined) return false;
  return data[key].options;
});

export const formatChildOptions = (data) => {
  const newData = { ...data };

  Object.keys(newData).forEach((key) => {
    const item = newData[key];

    if (item.options) {
      newData[key] = {
        ...item,
        options: formatOptions(item.options),
      };
    }
  });

  return newData;
};

// parser for he_special_service_us
export function parseSpecialServiceUSValues(basicInfo, type) {
  let services = {};
  const values = [];

  if (type === 'specialServicesIds') {
    services = {
      ...get(basicInfo, 'studies.studyOptions', {}),
      ...get(basicInfo, 'studentLife.services', {}),
    };
  } else {
    services = {
      ...get(basicInfo, 'studentLife.organizations', {}),
    };
  }
  Object.keys(studyOptionsSpecialServiceUSValues).forEach((k) => {
    if (get(services[k], 'value', '0') === '1') {
      values.push({
        target_id: studyOptionsSpecialServiceUSValues[k],
      });
    }
  });
  return values;
}

export function parseValue(value, key, basicInfo) {
  if (isObject(value)) {
    return get(value[key], 'targetId', false) ?
      // study options special case entity
      parseSpecialServiceUSValues(basicInfo, get(value[key], 'key', '')):
      get(value[key], 'value', value[key]);
  }
  return value;
}

/**
 * Hoc that uses basic info state and updates it
 * @param WrappedComponent: the react component we want to wrap
 * @param fieldName: property we want to get from state.basicInfo this is goint to be passed to get lodash
 * @param defaultValue: the default value to set if the field is falsy
 * @param mapper: the mapper object to help set data to hubs ui
 */
/* istanbul ignore next */
function withCollegeService(
  WrappedComponent, fieldName, defaultValue={}, mapper, optionsDependencies, collectionType, collectionReference,
) {
  return connect(
    (state) => ({ basicInfo: state.basicInfo }),
    {
      updateBasicInfo: updateBasicInfoAction,
      updateBasicInfoOptions: updateBasicInfoOptionsAction,
    },
  )(
    class CollegeServiceContainer extends React.Component {
      static propTypes= {
        basicInfo: PropTypes.object,
        updateBasicInfo: PropTypes.func,
        updateBasicInfoOptions: PropTypes.func,
      }

      fieldValue = null;

      componentWillMount() {
        this.getFieldData();
      }

      componentWillReceiveProps(nextProps) {
        if (this.props.basicInfo !== nextProps.basicInfo) {
          this.getFieldData(nextProps);
        }
      }

      getFieldData = (props = this.props) => {
        let data = get(props.basicInfo, fieldName, defaultValue);

        let options;
        if (data.options) {
          options = formatOptions(data.options);
        } else if (hasChildWithOptions(data)) {
          data = formatChildOptions(data);
        }

        if (Array.isArray(data)) {
          this.fieldValue = data.map((item) => decorateFieldWithValidation(fieldName, item));
        } else {
          this.fieldValue = decorateFieldWithValidation(fieldName, data, props.basicInfo);
          if (options != null) {
            this.fieldValue.options = options;
          }
        }
      };

      updateHubsUI = (value, order, fieldKey) => {
        if (order === undefined) {
          const { eid } = this.fieldValue;

          Object.keys(mapper).forEach((key) => {
            updateHubsUI({
              eid: eid || get(value[key], 'eid', ''),
              field: mapper[key],
              value: parseValue(value, key, this.props.basicInfo),
            });
          });
        } else {
          const { eid } = fieldKey ? this.fieldValue[fieldKey][order] : this.fieldValue[order];
          updateHubsUI({
            eid,
            field: get(mapper, value.name),
            value: value.value,
          });
        }
      }

      onChange = (e, value, fieldKey, order) => {
        const localFieldName = fieldKey ? `${fieldName}.${fieldKey}` : fieldName;

        this.props.updateBasicInfo({
          fieldName: localFieldName,
          value,
        });

        /* istanbul ignore else */
        if (optionsDependencies && localFieldName in optionsDependencies) {
          this.props.updateBasicInfoOptions({
            fieldName: optionsDependencies[localFieldName],
            value: parseValue(value, 'value'),
          });
        }

        if (fieldKey && order === undefined) {
          const { eid } = this.fieldValue[fieldKey];
          this.updateHubsUI({
            [fieldKey]: {
              ...value,
              eid,
            },
          });
        } else {
          this.updateHubsUI(value, order, fieldKey);
        }
      };

      render() {
        const { ...other } = this.props;
        this.collection = getFieldMetadata(collectionType, collectionReference);
        return (
          <WrappedComponent
            {...other}
            initialValue={this.fieldValue}
            onChange={this.onChange}
            collection={this.collection}
            mapper={mapper}
            fieldName={fieldName}
          />
        );
      }
    },
  );
}

export default withCollegeService;
